import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  ChevronLeft, 
  ChevronRight,
  Footprints,
  Calendar as CalendarIcon,
  Flame,
  Clock
} from 'lucide-react';
import { useTheme } from './ThemeProvider';


export default function WalkingCalendar({ onBack, isExpired, onNavigate }) {
  const { theme } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [walkingData, setWalkingData] = useState({});

  // Load walking data
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('gym_notebook_walking_daily_data') || '{}');
    setWalkingData(data);
  }, []);

  // Get calendar data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Week starts with Monday
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Adjust starting day for Monday start (0 = Monday, 6 = Sunday)
  const getAdjustedStartDay = () => {
    const day = firstDayOfMonth.getDay();
    return day === 0 ? 6 : day - 1; // Sunday (0) becomes 6, Monday (1) becomes 0, etc.
  };
  const adjustedStartDay = getAdjustedStartDay();

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Get walking data for a specific date
  const getWalkingForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return walkingData[dateStr] || null;
  };

  // Check if date is today
  const isToday = (day) => {
    const today = new Date();
    return today.getFullYear() === year && 
           today.getMonth() === month && 
           today.getDate() === day;
  };

  // Check if date is selected
  const isSelected = (day) => {
    if (!selectedDate) return false;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr === selectedDate;
  };

  // Handle date click
  const handleDateClick = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  // Get selected date's walking data
  const selectedWalkingData = selectedDate ? walkingData[selectedDate] : null;

  // Generate calendar days
  const calendarDays = [];
  
  // Empty cells for days before the first day of month (Monday start)
  for (let i = 0; i < adjustedStartDay; i++) {
    calendarDays.push(
      <div key={`empty-${i}`} className="h-12 sm:h-16"></div>
    );
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const walking = getWalkingForDate(day);
    const hasWalking = walking && walking.steps > 0;
    
    calendarDays.push(
      <button
        key={day}
        onClick={() => handleDateClick(day)}
        className={`h-12 sm:h-16 rounded-xl relative flex flex-col items-center justify-center transition-all ${
          isSelected(day)
            ? 'bg-blue-500 text-white ring-2 ring-blue-400 shadow-lg shadow-blue-500/20'
            : isToday(day)
            ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/50'
            : hasWalking
            ? 'bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50'
            : 'hover:bg-slate-800'
        }`}
      >
        <span className={`text-sm font-medium ${isSelected(day) ? 'text-white' : 'text-white'}`}>
          {day}
        </span>
        {hasWalking && (
          <div className="absolute bottom-1 flex items-center gap-0.5">
            <Footprints className={`h-3 w-3 ${isSelected(day) ? 'text-white' : 'text-blue-400'}`} />
            <span className={`text-[8px] ${isSelected(day) ? 'text-white/80' : 'text-slate-400'}`}>
              {walking.steps >= 1000 ? `${(walking.steps / 1000).toFixed(1)}k` : walking.steps}
            </span>
          </div>
        )}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-950 ios-header border-none" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <CalendarIcon className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-lg font-heading font-bold text-white">
              Calendar
            </h1>
          </div>
          <Button variant="ghost" size="sm" onClick={goToToday} className="text-blue-400 hover:bg-slate-800 rounded-xl">
            Today
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-6 pb-8 pt-20">
        <div className="max-w-lg mx-auto">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={goToPreviousMonth}
            className="text-white hover:bg-slate-800 rounded-xl"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <h2 className="text-xl font-heading font-bold text-white">
            {monthNames[month]} {year}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={goToNextMonth}
            className="text-white hover:bg-slate-800"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <Card className="bg-slate-900/80 border-slate-800 rounded-2xl mb-6">
          <CardContent className="p-3 sm:p-4">
            {/* Day Names */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="h-8 flex items-center justify-center">
                  <span className="text-xs text-slate-400 font-medium">{day}</span>
                </div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        {selectedDate && (
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <Footprints className="h-4 w-4 text-white" />
                </div>
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedWalkingData && selectedWalkingData.steps > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {/* Steps */}
                  <div className="text-center p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mx-auto mb-2">
                      <Footprints className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {selectedWalkingData.steps.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-400">Steps</p>
                  </div>
                  
                  {/* Distance */}
                  <div className="text-center p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center mx-auto mb-2">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {((selectedWalkingData.distance || 0) / 1000).toFixed(1)}
                    </p>
                    <p className="text-xs text-slate-400">km</p>
                  </div>
                  
                  {/* Calories */}
                  <div className="text-center p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center mx-auto mb-2">
                      <Flame className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {selectedWalkingData.calories || 0}
                    </p>
                    <p className="text-xs text-slate-400">kcal</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-3">
                    <Footprints className="h-8 w-8 text-slate-600" />
                  </div>
                  <p className="text-slate-400">No walking recorded</p>
                  <p className="text-slate-500 text-sm">Start a walking session to track your steps</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Monthly Summary */}
        <Card className="bg-slate-900/80 border-slate-800 rounded-2xl mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <CalendarIcon className="h-4 w-4 text-white" />
              </div>
              Monthly Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              // Calculate monthly totals
              let totalSteps = 0;
              let totalCalories = 0;
              let activeDays = 0;
              
              for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const data = walkingData[dateStr];
                if (data && data.steps > 0) {
                  totalSteps += data.steps;
                  totalCalories += data.calories || 0;
                  activeDays++;
                }
              }
              
              return (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <p className="text-2xl font-bold text-blue-400">
                      {totalSteps >= 1000 ? `${(totalSteps / 1000).toFixed(1)}k` : totalSteps}
                    </p>
                    <p className="text-xs text-slate-400">Total Steps</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <p className="text-2xl font-bold text-orange-400">
                      {totalCalories.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-400">Calories Burned</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <p className="text-2xl font-bold text-green-400">
                      {activeDays}
                    </p>
                    <p className="text-xs text-slate-400">Active Days</p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
        </div>
      </main>
    </div>
  );
}
